"use client";

import {
  SquaresFour,
  BookOpen,
  VideoCamera,
  FileText,
  GearSix,
  GraduationCap,
  SignOut,
  CaretLeft,
  CaretRight,
  ClipboardText,
  Note,
  ChatCircleDots,
  ShieldCheck,
  Lightning,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import SidebarItem from "./SidebarItem";

const learnNav = [
  { href: "/dashboard", icon: SquaresFour, label: "Dashboard" },
  { href: "/classrooms", icon: GraduationCap, label: "Courses" },
  { href: "/content", icon: BookOpen, label: "Study Notes" },
  { href: "/classes", icon: VideoCamera, label: "Recordings" },
  { href: "/exam", icon: FileText, label: "Exams" },
  { href: "/exam/generate", icon: Lightning, label: "Custom Exam" },
  { href: "/syllabus", icon: ClipboardText, label: "Syllabus" },
  { href: "/notes", icon: Note, label: "My Notes" },
];

const accountNav = [
  { href: "/profile", icon: GearSix, label: "Profile" },
  { href: "/contact", icon: ChatCircleDots, label: "Help" },
];


interface Props {
  activePath?: string;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onLogout?: () => void;
  isAdmin?: boolean;
}

export default function Sidebar({
  activePath = "/dashboard",
  mobileOpen = false,
  onMobileClose,
  collapsed,
  onToggleCollapse,
  onLogout,
  isAdmin = false,
}: Props) {
  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onMobileClose}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col",
          "bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800",
          "transition-[width,transform] duration-300 ease-in-out",
          collapsed ? "w-20" : "w-64",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* ── Logo / header ── */}
        <div
          className={cn(
            "flex items-center border-b border-primary-200 dark:border-primary-900/40 px-4 py-4",
            collapsed ? "justify-center" : "justify-between",
          )}
        >
          {/* Logo area: app icon when collapsed, wordmark when expanded */}
          {collapsed ? (
            <div className="flex flex-col items-center gap-1">
              <div className="h-10 w-10 rounded-2xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/img/logo/App_Icon_Primary.png"
                  alt="Clamphook"
                  className="size-10 object-contain rounded-2xl"
                />
              </div>
              <button
                onClick={onToggleCollapse}
                className="flex items-center justify-center rounded-lg p-1 w-full text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary dark:hover:text-primary-300 transition-colors"
                aria-label="Expand sidebar"
              >
                <CaretRight size={16} />
              </button>
            </div>
          ) : (
            <>
              {/* brightness-0 zeroes all channels → invert flips to pure white, no colour cast */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/img/logo/ClampHook_Logo_Primary.png"
                alt="Clamphook"
                className="h-6 object-contain dark:brightness-0 dark:invert"
              />
            </>
          )}

          {/* Collapse toggle — only shown when expanded */}
          {!collapsed && (
            <button
              onClick={() => {
                if (typeof window !== "undefined" && window.innerWidth < 1024) {
                  onMobileClose?.();
                } else {
                  onToggleCollapse();
                }
              }}
              className="flex items-center justify-center rounded-lg p-1.5 flex-shrink-0 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-primary dark:hover:text-primary-300 transition-colors"
              aria-label="Collapse sidebar"
            >
              <CaretLeft size={18} />
            </button>
          )}
        </div>

        {/* ── Nav ── */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-6">
          <section>
            {!collapsed && (
              <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Learn
              </p>
            )}
            <ul className="space-y-0.5">
              {learnNav.map(({ href, icon, label }) => (
                <SidebarItem
                  key={href}
                  href={href}
                  icon={icon}
                  label={label}
                  collapsed={collapsed}
                  active={activePath === href}
                />
              ))}
            </ul>
          </section>

          <section>
            {!collapsed && (
              <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Account
              </p>
            )}
            <ul className="space-y-0.5">
              {accountNav.map(({ href, icon, label }) => (
                <SidebarItem
                  key={href}
                  href={href}
                  icon={icon}
                  label={label}
                  collapsed={collapsed}
                  active={activePath === href}
                />
              ))}
            </ul>
          </section>

          {isAdmin && (
            <section>
              {!collapsed && (
                <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  Admin
                </p>
              )}
              <ul className="space-y-0.5">
                <SidebarItem
                  href="/admin"
                  icon={ShieldCheck}
                  label="Admin Panel"
                  collapsed={collapsed}
                  active={activePath.startsWith("/admin")}
                />
              </ul>
            </section>
          )}
        </nav>

        {/* ── Footer: logout ── */}
        <div className="border-t border-gray-200 dark:border-gray-800 px-3 py-3">

          <button
            onClick={onLogout}
            title={collapsed ? "Log out" : undefined}
            className={cn(
              "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium",
              "text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-500 transition-colors",
              collapsed && "justify-center",
            )}
          >
            <SignOut size={18} className="flex-shrink-0" />
            {!collapsed && <span>Log out</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
